import { useState, useEffect } from "react";
import type { Order, OrderStatus } from "../lib/data";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";
import {
	Package,
	Clock,
	Printer,
	CheckCircle2,
	XCircle,
	FileBox,
	DollarSign,
	Shield,
	RefreshCw,
	Star,
	Truck,
} from "lucide-react";

interface OrderTrackingProps {
	onWriteReview: (orderId: string, printerId: string) => void;
}

const statusConfig: Record<OrderStatus, { label: string; color: string; icon: typeof Package }> = {
	pending: { label: "Pending", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200", icon: Clock },
	accepted: { label: "Accepted", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200", icon: CheckCircle2 },
	printing: { label: "Printing", color: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200", icon: Printer },
	ready: { label: "Ready", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200", icon: Truck },
	completed: { label: "Completed", color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200", icon: CheckCircle2 },
	cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200", icon: XCircle },
};

const statusOrder: OrderStatus[] = ["pending", "accepted", "printing", "ready", "completed"];

export function OrderTracking({ onWriteReview }: OrderTrackingProps) {
	const [orders, setOrders] = useState<Order[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectedOrder, setSelectedOrder] = useState<string | null>(null);

	const fetchOrders = async () => {
		setLoading(true);
		try {
			const res = await fetch("/api/orders");
			const data = await res.json();
			setOrders(data);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchOrders();
	}, []);

	const updateStatus = async (orderId: string, status: OrderStatus, note: string) => {
		await fetch(`/api/orders/${orderId}/status`, {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ status, note }),
		});
		fetchOrders();
	};

	const activeOrder = selectedOrder ? orders.find((o) => o.id === selectedOrder) : null;

	if (loading) {
		return (
			<div className="flex items-center justify-center py-20">
				<RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (orders.length === 0) {
		return (
			<Card>
				<CardContent className="p-12 text-center">
					<Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
					<h2 className="text-xl font-semibold mb-2">No Orders Yet</h2>
					<p className="text-muted-foreground">Your orders will appear here once you request a print.</p>
				</CardContent>
			</Card>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<h2 className="text-2xl font-bold">My Orders</h2>
				<Button variant="outline" size="sm" onClick={fetchOrders} className="gap-2">
					<RefreshCw className="w-3.5 h-3.5" />
					Refresh
				</Button>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Order list */}
				<div className="lg:col-span-1 space-y-3">
					{orders.map((order) => {
						const config = statusConfig[order.status];
						const Icon = config.icon;
						return (
							<Card
								key={order.id}
								className={`cursor-pointer transition-all hover:shadow-md ${
									selectedOrder === order.id ? "border-primary ring-2 ring-primary/20" : ""
								}`}
								onClick={() => setSelectedOrder(order.id)}
							>
								<CardContent className="p-4">
									<div className="flex items-start justify-between mb-2">
										<div className="flex items-center gap-2">
											<FileBox className="w-4 h-4 text-muted-foreground" />
											<span className="text-sm font-medium truncate max-w-[150px]">
												{order.fileName}
											</span>
										</div>
										<Badge className={config.color + " text-xs"}>
											{config.label}
										</Badge>
									</div>
									<div className="text-xs text-muted-foreground space-y-1">
										<div className="flex justify-between">
											<span>{order.printerName}</span>
											<span>${order.estimatedPrice.toFixed(2)}</span>
										</div>
										<div className="flex justify-between">
											<span>{order.material} · {order.color}</span>
											<span>x{order.quantity}</span>
										</div>
									</div>
								</CardContent>
							</Card>
						);
					})}
				</div>

				{/* Order detail */}
				<div className="lg:col-span-2">
					{activeOrder ? (
						<div className="space-y-4">
							{/* Order header */}
							<Card>
								<CardContent className="p-6">
									<div className="flex items-start justify-between mb-4">
										<div>
											<h3 className="text-lg font-semibold">{activeOrder.fileName}</h3>
											<p className="text-sm text-muted-foreground">
												Order #{activeOrder.id} · {new Date(activeOrder.createdAt).toLocaleDateString()}
											</p>
										</div>
										<Badge className={statusConfig[activeOrder.status].color + " text-sm px-3 py-1"}>
											{statusConfig[activeOrder.status].label}
										</Badge>
									</div>

									{/* Status timeline */}
									<div className="relative mt-6">
										<div className="flex justify-between mb-2">
											{statusOrder.map((s, i) => {
												const currentIdx = statusOrder.indexOf(activeOrder.status);
												const isPast = i <= currentIdx;
												const isCurrent = i === currentIdx;
												const Config = statusConfig[s];
												const SIcon = Config.icon;

												return (
													<div key={s} className="flex flex-col items-center flex-1">
														<div
															className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
																isPast
																	? "bg-primary border-primary text-primary-foreground"
																	: "bg-muted border-border text-muted-foreground"
															} ${isCurrent ? "ring-4 ring-primary/20" : ""}`}
														>
															<SIcon className="w-4 h-4" />
														</div>
														<span
															className={`text-xs mt-2 ${
																isCurrent ? "font-medium text-foreground" : "text-muted-foreground"
															}`}
														>
															{Config.label}
														</span>
													</div>
												);
											})}
										</div>
										{/* Progress bar */}
										<div className="absolute top-5 left-[10%] right-[10%] h-0.5 bg-border -z-10">
											<div
												className="h-full bg-primary transition-all duration-500"
												style={{
													width: `${(statusOrder.indexOf(activeOrder.status) / (statusOrder.length - 1)) * 100}%`,
												}}
											/>
										</div>
									</div>
								</CardContent>
							</Card>

							{/* Order details */}
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<Card>
									<CardContent className="p-4">
										<h4 className="text-sm font-medium mb-3 flex items-center gap-2">
											<FileBox className="w-4 h-4 text-muted-foreground" />
											Print Details
										</h4>
										<div className="space-y-2 text-sm">
											<div className="flex justify-between">
												<span className="text-muted-foreground">Material</span>
												<span>{activeOrder.material}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-muted-foreground">Color</span>
												<span>{activeOrder.color}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-muted-foreground">Quantity</span>
												<span>{activeOrder.quantity}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-muted-foreground">Dimensions</span>
												<span>
													{activeOrder.dimensions.x}x{activeOrder.dimensions.y}x
													{activeOrder.dimensions.z}mm
												</span>
											</div>
											<div className="flex justify-between">
												<span className="text-muted-foreground">Weight</span>
												<span>{activeOrder.weight}g</span>
											</div>
										</div>
									</CardContent>
								</Card>

								<Card>
									<CardContent className="p-4">
										<h4 className="text-sm font-medium mb-3 flex items-center gap-2">
											<DollarSign className="w-4 h-4 text-muted-foreground" />
											Payment
										</h4>
										<div className="space-y-2 text-sm">
											<div className="flex justify-between">
												<span className="text-muted-foreground">Estimated Price</span>
												<span className="font-semibold">${activeOrder.estimatedPrice.toFixed(2)}</span>
											</div>
											{activeOrder.finalPrice > 0 && (
												<div className="flex justify-between">
													<span className="text-muted-foreground">Final Price</span>
													<span className="font-semibold">${activeOrder.finalPrice.toFixed(2)}</span>
												</div>
											)}
											<Separator />
											<div className="flex items-center gap-2">
												<Shield className={`w-4 h-4 ${activeOrder.escrowHeld ? "text-blue-500" : "text-green-500"}`} />
												<span className="text-xs">
													{activeOrder.escrowHeld
														? "Payment held in escrow"
														: "Payment released to printer"}
												</span>
											</div>
										</div>
									</CardContent>
								</Card>
							</div>

							{/* Tracking history */}
							<Card>
								<CardContent className="p-6">
									<h4 className="text-sm font-medium mb-4 flex items-center gap-2">
										<Clock className="w-4 h-4 text-muted-foreground" />
										Activity Timeline
									</h4>
									<div className="space-y-4">
										{activeOrder.trackingHistory.map((event, i) => (
											<div key={i} className="flex gap-3">
												<div className="flex flex-col items-center">
													<div
														className={`w-3 h-3 rounded-full ${
															i === activeOrder.trackingHistory.length - 1
																? "bg-primary"
																: "bg-muted-foreground/30"
														}`}
													/>
													{i < activeOrder.trackingHistory.length - 1 && (
														<div className="w-px h-full bg-border min-h-[24px]" />
													)}
												</div>
												<div className="pb-4">
													<p className="text-sm font-medium">{event.note}</p>
													<p className="text-xs text-muted-foreground">
														{new Date(event.timestamp).toLocaleString()}
													</p>
												</div>
											</div>
										))}
									</div>
								</CardContent>
							</Card>

							{/* Actions */}
							{activeOrder.status === "ready" && (
								<div className="flex gap-3">
									<Button
										className="flex-1"
										onClick={() => updateStatus(activeOrder.id, "completed", "Order confirmed as received")}
									>
										<CheckCircle2 className="w-4 h-4 mr-2" />
										Confirm Delivery
									</Button>
								</div>
							)}
							{activeOrder.status === "completed" && (
								<Button
									variant="outline"
									className="gap-2"
									onClick={() => onWriteReview(activeOrder.id, activeOrder.printerId)}
								>
									<Star className="w-4 h-4" />
									Write a Review
								</Button>
							)}
						</div>
					) : (
						<Card>
							<CardContent className="p-12 text-center">
								<Package className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
								<p className="text-muted-foreground">Select an order to view details</p>
							</CardContent>
						</Card>
					)}
				</div>
			</div>
		</div>
	);
}
